import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Form, FloatingLabel } from "react-bootstrap";
import { getToken } from "../../utilities/authHelper";
import { fetchDashboards } from "../../utilities/AdminUtilities";

function DashboardDetails() {
  const [dashboards, setDashboards] = useState([]);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editable, setEditable] = useState(false);
  const [editedDashboard, setEditedDashboard] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isCreatingNewDashboard, setIsCreatingNewDashboard] = useState(false);

  useEffect(() => {
    fetchDashboards(setDashboards);
  }, []);

  const handleDashboardClick = (dashboard) => {
    setSelectedDashboard(dashboard);
    setEditedDashboard({ ...dashboard });
    setShowModal(true);
  };

  const handleEditChange = (e) => {
    setEditedDashboard({
      ...editedDashboard,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
      let detailsObject = editedDashboard.details_object;
      if (typeof detailsObject === "string") {
        try {
          detailsObject = JSON.parse(detailsObject);
        } catch (error) {
          console.error("Invalid JSON string in details_object:", error);
          detailsObject = {}; // Default to an empty object if parsing fails
        }
      }

      // Prepare the updated dashboard object
      const updatedDashboard = {
        ...editedDashboard,
        details_object: detailsObject,
      };
      const response = await fetch(
        `${apiUrl}/admin/create_or_update_dashboard${
          isCreatingNewDashboard ? "" : `?dashboard_id=${selectedDashboard.id}`
        }`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(updatedDashboard),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save dashboard");
      }

      setShowSuccessModal(true);
      handleCloseModal();
      fetchDashboards(setDashboards);
    } catch (error) {
      console.error("Error saving dashboard:", error);
    }
  };

  const handleCreateNewDashboard = () => {
    setIsCreatingNewDashboard(true);
    setEditedDashboard({});
    setShowModal(true);
    setEditable(true);
  };

  const handleCloseModal = () => {
    setEditable(false);
    setShowModal(false);
    setIsCreatingNewDashboard(false);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };
  const handleDetailsChange = (e) => {
    setEditedDashboard({
      ...editedDashboard,
      details_object: (e.target.value),
    });
  };

  return (
    <>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Dashboard Name</th>
            <th>Category</th>
            <th>Status</th>
            <th>Data Reference</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {dashboards.map((dashboard) => (
            <tr key={dashboard.id}>
              <td>{dashboard.dashboard_name}</td>
              <td>{dashboard.dashboard_category}</td>
              <td>{dashboard.dashboard_status}</td>
              <td>{dashboard.data_reference}</td>
              <td>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleDashboardClick(dashboard)}
                >
                  Edit
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header>
          <Modal.Title>
            {isCreatingNewDashboard
              ? "Create New Dashboard"
              : selectedDashboard?.dashboard_name}
          </Modal.Title>
          <i
            className="fas fa-edit ml-2"
            style={{ cursor: "pointer" }}
            onClick={() => setEditable(!editable)}
          ></i>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Form groups for each dashboard attribute */}
            <Form.Group className="mb-3" controlId="dashboardName">
              <FloatingLabel controlId="dashboardName" label="Dashboard Name">
                <Form.Control
                  type="text"
                  name="dashboard_name"
                  value={editedDashboard.dashboard_name || ""}
                  placeholder="Dashboard Name"
                  onChange={handleEditChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3" controlId="dashboardCategory">
              <FloatingLabel
                controlId="dashboardCategory"
                label="Dashboard Category"
              >
                <Form.Control
                  type="text"
                  name="dashboard_category"
                  value={editedDashboard.dashboard_category || ""}
                  placeholder="Dashboard Category"
                  onChange={handleEditChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3" controlId="dashboardStatus">
              <FloatingLabel
                controlId="dashboardStatus"
                label="Dashboard Status"
              >
                <Form.Control
                  type="text"
                  name="dashboard_status"
                  value={editedDashboard.dashboard_status || ""}
                  placeholder="Dashboard Status"
                  onChange={handleEditChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3" controlId="dataReference">
              <FloatingLabel controlId="dataReference" label="Data Reference">
                <Form.Control
                  type="text"
                  name="data_reference"
                  value={editedDashboard.data_reference || ""}
                  placeholder="Data Reference"
                  onChange={handleEditChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3" controlId="dataReferenceType">
              <FloatingLabel
                controlId="dataReferenceType"
                label="Data Reference Type"
              >
                <Form.Control
                  type="text"
                  name="data_reference_type"
                  value={editedDashboard.data_reference_type || ""}
                  placeholder="Data Reference Type"
                  onChange={handleEditChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3" controlId="dashboardDetails">
              <FloatingLabel
                controlId="details_object"
                label="Dashboard Details"
              >
                <Form.Control
                  as="textarea"
                  name="dashboard_details"
                  value={editedDashboard.details_object || ""}
                  placeholder="Dashboard Details"
                  onChange={handleDetailsChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3" controlId="creationTimestamp">
              <FloatingLabel
                controlId="creationTimestamp"
                label="Creation Timestamp"
              >
                <Form.Control
                  type="text"
                  name="creation_timestamp"
                  value={editedDashboard.creation_timestamp || ""}
                  placeholder="Creation Timestamp"
                  disabled={true} // Non-editable
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3" controlId="updationTimestamp">
              <FloatingLabel
                controlId="updationTimestamp"
                label="Updation Timestamp"
              >
                <Form.Control
                  type="text"
                  name="updation_timestamp"
                  value={editedDashboard.updation_timestamp || ""}
                  placeholder="Updation Timestamp"
                  disabled={true} // Non-editable
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3" controlId="createdBy">
              <FloatingLabel controlId="createdBy" label="Created By">
                <Form.Control
                  type="text"
                  name="created_by"
                  value={editedDashboard.created_by || ""}
                  placeholder="Created By"
                  disabled={true} // Non-editable
                />
              </FloatingLabel>
            </Form.Group>
          </Form>
        </Modal.Body>
        {editable && (
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Close
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      <Modal show={showSuccessModal} onHide={handleCloseSuccessModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>Your changes have been saved successfully.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSuccessModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="p-3 d-flex justify-content-center align-items-center">
        <Button variant="primary" onClick={handleCreateNewDashboard}>
          Create New Dashboard
        </Button>
      </div>
    </>
  );
}

export default DashboardDetails;
